import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Bell, BellRing, BellOff, CheckCheck, Sparkles, AlertTriangle, CalendarDays, FileText, CreditCard, Info, Wrench, MessageSquare, UserCheck, Zap } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { api, buildUrl } from "@shared/routes";
import type { Notification } from "@shared/schema";
import { usePushSubscription } from "@/hooks/use-push-notifications";
import { useRealtimeEvents } from "@/hooks/use-realtime-events";
import { useLocation } from "wouter";

function getRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay}d ago`;
}

function getTypeIcon(type: string) {
  switch (type) {
    case "job_assigned":
    case "job_urgent":
    case "cleaning_assigned":
    case "cleaning_completed":
      return <Sparkles className="w-4 h-4 text-primary" />;
    case "checkout_submitted":
      return <FileText className="w-4 h-4 text-green-600 dark:text-green-400" />;
    case "reservation_changed":
    case "reservation_created":
    case "reservation_updated":
      return <CalendarDays className="w-4 h-4 text-blue-600 dark:text-blue-400" />;
    case "invoice_issued":
      return <CreditCard className="w-4 h-4 text-orange-600 dark:text-orange-400" />;
    case "ticket_created":
    case "ticket_updated":
    case "guest_reply":
      return <MessageSquare className="w-4 h-4 text-purple-600 dark:text-purple-400" />;
    case "maintenance_assigned":
    case "maintenance_completed":
      return <Wrench className="w-4 h-4 text-teal-600 dark:text-teal-400" />;
    case "owner_reservation":
      return <UserCheck className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />;
    case "automation":
      return <Zap className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />;
    case "urgent":
      return <AlertTriangle className="w-4 h-4 text-destructive" />;
    default:
      return <Info className="w-4 h-4 text-muted-foreground" />;
  }
}

function getNotificationLink(notification: Notification): string | null {
  const data = notification.data as Record<string, any> | null;
  const type = notification.type;

  if (data?.url) return data.url;

  switch (type) {
    case "reservation_created":
    case "reservation_updated":
    case "reservation_changed":
    case "owner_reservation":
      return "/admin/reservations";
    case "job_assigned":
    case "job_urgent":
    case "cleaning_assigned":
    case "cleaning_completed":
      return "/admin/cleaning-jobs";
    case "ticket_created":
    case "ticket_updated":
    case "guest_reply":
      return "/admin/tickets";
    case "maintenance_assigned":
    case "maintenance_completed":
      return "/admin/maintenance";
    case "invoice_issued":
      return "/admin/finances";
    case "checkout_submitted":
      return "/admin/submissions";
    case "automation":
      return "/admin/automation";
    default:
      return null;
  }
}

function playNotificationSound() {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const notes = [
      { freq: 587.33, start: 0, dur: 0.12 },
      { freq: 783.99, start: 0.13, dur: 0.12 },
      { freq: 1046.5, start: 0.26, dur: 0.18 },
    ];
    notes.forEach(({ freq, start, dur }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.15, ctx.currentTime + start);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + dur);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + dur + 0.05);
    });
    setTimeout(() => ctx.close(), 1000);
  } catch (_) {}
}

function groupByDate(notifications: Notification[]): { label: string; items: Notification[] }[] {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86400000);
  const weekAgo = new Date(today.getTime() - 7 * 86400000);

  const groups: { label: string; items: Notification[] }[] = [];
  const todayItems: Notification[] = [];
  const yesterdayItems: Notification[] = [];
  const thisWeekItems: Notification[] = [];
  const olderItems: Notification[] = [];

  for (const n of notifications) {
    const d = n.createdAt ? new Date(n.createdAt) : new Date(0);
    if (d >= today) todayItems.push(n);
    else if (d >= yesterday) yesterdayItems.push(n);
    else if (d >= weekAgo) thisWeekItems.push(n);
    else olderItems.push(n);
  }

  if (todayItems.length > 0) groups.push({ label: "Today", items: todayItems });
  if (yesterdayItems.length > 0) groups.push({ label: "Yesterday", items: yesterdayItems });
  if (thisWeekItems.length > 0) groups.push({ label: "This Week", items: thisWeekItems });
  if (olderItems.length > 0) groups.push({ label: "Older", items: olderItems });

  return groups;
}

const originalTitle = typeof document !== 'undefined' ? document.title : 'Staio';

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { isSupported, isSubscribed, subscribe, unsubscribe, isLoading: pushLoading } = usePushSubscription();
  const prevUnreadRef = useRef<number | null>(null);

  useRealtimeEvents("notification", () => {
    playNotificationSound();
  });

  const { data: notifications = [] } = useQuery<Notification[]>({
    queryKey: [api.notifications.list.path],
    queryFn: async () => {
      const res = await fetch(api.notifications.list.path, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch notifications");
      return res.json();
    },
  });

  const { data: unreadData } = useQuery<{ count: number }>({
    queryKey: [api.notifications.unreadCount.path],
    queryFn: async () => {
      const res = await fetch(api.notifications.unreadCount.path, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch unread count");
      return res.json();
    },
    refetchInterval: 60000,
  });

  const unreadCount = unreadData?.count ?? 0;

  useEffect(() => {
    if (unreadCount > 0) {
      document.title = `(${unreadCount}) ${originalTitle}`;
    } else {
      document.title = originalTitle;
    }
    return () => { document.title = originalTitle; };
  }, [unreadCount]);

  const markRead = useMutation({
    mutationFn: async (id: number) => {
      const url = buildUrl(api.notifications.markRead.path, { id });
      const res = await fetch(url, { method: "POST", credentials: "include" });
      if (!res.ok) throw new Error("Failed to mark notification as read");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.notifications.list.path] });
      queryClient.invalidateQueries({ queryKey: [api.notifications.unreadCount.path] });
    },
  });

  const markAllRead = useMutation({
    mutationFn: async () => {
      const res = await fetch(api.notifications.markAllRead.path, { method: "POST", credentials: "include" });
      if (!res.ok) throw new Error("Failed to mark all as read");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [api.notifications.list.path] });
      queryClient.invalidateQueries({ queryKey: [api.notifications.unreadCount.path] });
    },
  });

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.readAt) {
      markRead.mutate(notification.id);
    }
    const link = getNotificationLink(notification);
    if (link) {
      setOpen(false);
      setLocation(link);
    }
  };

  const handlePushToggle = async () => {
    try {
      if (isSubscribed) {
        await unsubscribe();
      } else {
        await subscribe();
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Could not update notification settings";
      toast({
        title: "Notifications",
        description: message.includes("permission") || message.includes("denied")
          ? "Notification permission was denied. Please allow notifications in your browser settings."
          : "Could not enable notifications. Please try again.",
        variant: "destructive",
      });
    }
  };

  const grouped = groupByDate(notifications);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="button-notification-bell">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 min-w-5 p-0 flex items-center justify-center text-xs"
              data-testid="badge-unread-count"
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between gap-2 p-3 border-b">
          <h4 className="font-semibold text-sm" data-testid="text-notifications-title">Notifications</h4>
          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => markAllRead.mutate()}
                disabled={markAllRead.isPending}
                data-testid="button-mark-all-read"
              >
                <CheckCheck className="w-4 h-4 mr-1" />
                Mark all read
              </Button>
            )}
          </div>
        </div>

        {isSupported && (
          <div className="px-3 py-2 border-b bg-muted/30">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {isSubscribed ? (
                  <BellRing className="w-4 h-4 text-green-600" />
                ) : (
                  <BellOff className="w-4 h-4 text-muted-foreground" />
                )}
                <span className="text-xs font-medium" data-testid="text-push-status">
                  {isSubscribed ? "Push notifications ON" : "Push notifications OFF"}
                </span>
              </div>
              <Button
                variant={isSubscribed ? "outline" : "default"}
                size="sm"
                onClick={handlePushToggle}
                disabled={pushLoading}
                className="text-xs h-7"
                data-testid="button-push-toggle"
              >
                {pushLoading ? "..." : isSubscribed ? "Disable" : "Enable"}
              </Button>
            </div>
            {!isSubscribed && (
              <p className="text-xs text-muted-foreground mt-1">
                Get notified on your phone or desktop for reservations, tickets, and more
              </p>
            )}
          </div>
        )}

        <div className="max-h-80 overflow-y-auto" data-testid="list-notifications">
          {notifications.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground" data-testid="text-no-notifications">
              No notifications yet
            </div>
          ) : (
            grouped.map((group) => (
              <div key={group.label}>
                <div className="px-3 py-1.5 bg-muted/40 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground sticky top-0">
                  {group.label}
                </div>
                {group.items.map((notification) => {
                  const link = getNotificationLink(notification);
                  return (
                    <button
                      key={notification.id}
                      className={`w-full text-left p-3 border-b last:border-0 flex items-start gap-3 transition-colors hover:bg-muted/30 ${
                        !notification.readAt ? "bg-primary/5" : ""
                      } ${link ? "cursor-pointer" : ""}`}
                      onClick={() => handleNotificationClick(notification)}
                      data-testid={`notification-item-${notification.id}`}
                    >
                      <div className="mt-0.5 shrink-0">
                        {getTypeIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${!notification.readAt ? "font-medium" : "text-muted-foreground"}`} data-testid={`text-notification-title-${notification.id}`}>
                          {notification.title}
                        </p>
                        {notification.body && (
                          <p className="text-xs text-muted-foreground mt-0.5 truncate" data-testid={`text-notification-body-${notification.id}`}>
                            {notification.body}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1" data-testid={`text-notification-time-${notification.id}`}>
                          {notification.createdAt ? getRelativeTime(new Date(notification.createdAt)) : ""}
                        </p>
                      </div>
                      {!notification.readAt && (
                        <div className="w-2 h-2 rounded-full bg-primary mt-1.5 shrink-0" data-testid={`indicator-unread-${notification.id}`} />
                      )}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
